import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Ban, Check, X, Trash2, Pencil } from 'lucide-react';
import { API_BASE_URL } from '../../config';

const PartnerBotDetailPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [bot, setBot] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [togglingBot, setTogglingBot] = useState(false);
    const [deductAmount, setDeductAmount] = useState('');
    const [deductNote, setDeductNote] = useState('');
    const [deducting, setDeducting] = useState(false);
    const [processingId, setProcessingId] = useState(null);
    const [me, setMe] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [utmLinks, setUtmLinks] = useState([]);
    const [utmName, setUtmName] = useState('');
    const [utmKey, setUtmKey] = useState('');
    const [utmSource, setUtmSource] = useState('');
    const [utmMedium, setUtmMedium] = useState('');
    const [utmCampaign, setUtmCampaign] = useState('');
    const [creatingUtm, setCreatingUtm] = useState(false);
    const [utmDateFrom, setUtmDateFrom] = useState('');
    const [utmDateTo, setUtmDateTo] = useState('');
    const [editingUtm, setEditingUtm] = useState(null);

    const loadMe = async () => {
        try {
            const r = await fetch(`${API_BASE_URL}/api/auth/me`, { credentials: 'include' });
            if (r.ok) setMe(await r.json());
        } catch (_) {}
    };

    const loadBot = async () => {
        if (!id) return;
        try {
            setLoading(true);
            setError(null);
            const r = await fetch(`${API_BASE_URL}/api/referrals/bots/${id}`, {
                method: 'GET',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
            });
            if (!r.ok) {
                const data = await r.json().catch(() => ({}));
                throw new Error(data.detail || 'Бот не найден');
            }
            setBot(await r.json());
        } catch (e) {
            setError(e.message || 'Ошибка загрузки');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadMe(); }, []);
    useEffect(() => { loadBot(); }, [id]);

    const loadUtmLinks = async () => {
        if (!id) return;
        try {
            const params = new URLSearchParams();
            if (utmDateFrom && utmDateTo) {
                params.set('date_from', `${utmDateFrom}T00:00:00`);
                params.set('date_to', `${utmDateTo}T23:59:59`);
            }
            const qs = params.toString();
            const r = await fetch(`${API_BASE_URL}/api/referrals/bots/${id}/utm-links${qs ? `?${qs}` : ''}`, {
                method: 'GET',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
            });
            if (!r.ok) return;
            const data = await r.json();
            setUtmLinks(data.items || []);
        } catch (_) {}
    };
    useEffect(() => { loadUtmLinks(); }, [id, utmDateFrom, utmDateTo]);

    const handleDelete = async () => {
        if (!bot) return;
        if (!confirm(`Удалить партнёрского бота @${bot.bot_username || bot.id}? Заказы и заявки на вывод сохранятся.`)) return;
        setDeleting(true);
        try {
            const r = await fetch(`${API_BASE_URL}/api/referrals/bots/${bot.id}`, {
                method: 'DELETE',
                credentials: 'include',
            });
            if (!r.ok) {
                const data = await r.json().catch(() => ({}));
                throw new Error(data.detail || 'Ошибка удаления');
            }
            navigate('/partner-bots');
        } catch (e) {
            alert(e.message || 'Ошибка');
        } finally {
            setDeleting(false);
        }
    };

    const handleCreateUtm = async () => {
        if (!utmName.trim() || !utmKey.trim()) {
            alert('Заполните название и UTM key');
            return;
        }
        setCreatingUtm(true);
        try {
            const r = await fetch(`${API_BASE_URL}/api/referrals/bots/${id}/utm-links`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: utmName.trim(),
                    utm_key: utmKey.trim(),
                    utm_source: utmSource.trim() || null,
                    utm_medium: utmMedium.trim() || null,
                    utm_campaign: utmCampaign.trim() || null,
                }),
            });
            if (!r.ok) {
                const data = await r.json().catch(() => ({}));
                throw new Error(data.detail || 'Ошибка создания UTM');
            }
            setUtmName('');
            setUtmKey('');
            setUtmSource('');
            setUtmMedium('');
            setUtmCampaign('');
            await loadUtmLinks();
        } catch (e) {
            alert(e.message || 'Ошибка создания UTM');
        } finally {
            setCreatingUtm(false);
        }
    };

    const handleDeleteUtm = async (utmId) => {
        if (!confirm('Удалить UTM-ссылку? Статистика по ссылке в таблице пропадёт; в старых заказах метка может остаться.')) return;
        try {
            const r = await fetch(`${API_BASE_URL}/api/referrals/bots/${id}/utm-links/${utmId}`, {
                method: 'DELETE',
                credentials: 'include',
            });
            if (!r.ok) {
                const data = await r.json().catch(() => ({}));
                throw new Error(data.detail || 'Ошибка удаления');
            }
            setEditingUtm(null);
            await loadUtmLinks();
        } catch (e) {
            alert(e.message || 'Ошибка');
        }
    };

    const handleSaveUtmEdit = async () => {
        if (!editingUtm) return;
        try {
            const r = await fetch(`${API_BASE_URL}/api/referrals/bots/${id}/utm-links/${editingUtm.id}`, {
                method: 'PATCH',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: editingUtm.name,
                    utm_source: editingUtm.utm_source || null,
                    utm_medium: editingUtm.utm_medium || null,
                    utm_campaign: editingUtm.utm_campaign || null,
                    utm_content: editingUtm.utm_content || null,
                    utm_term: editingUtm.utm_term || null,
                }),
            });
            if (!r.ok) {
                const data = await r.json().catch(() => ({}));
                throw new Error(data.detail || 'Ошибка сохранения');
            }
            setEditingUtm(null);
            await loadUtmLinks();
        } catch (e) {
            alert(e.message || 'Ошибка');
        }
    };

    /** Отключить партнёрского бота: токен перестаёт обслуживаться runner/API (не блокируем пользователя в системе). */
    const handleDeactivateBot = async () => {
        if (!bot) return;
        if (!confirm('Отключить бота? Токен перестанет работать в runner; пользователь останется в системе.')) return;
        setTogglingBot(true);
        try {
            const r = await fetch(`${API_BASE_URL}/api/referrals/bots/${bot.id}`, {
                method: 'PATCH',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ is_active: false }),
            });
            if (!r.ok) {
                const d = await r.json().catch(() => ({}));
                throw new Error(d.detail || 'Ошибка');
            }
            await loadBot();
        } catch (e) {
            alert(e.message || 'Ошибка');
        } finally {
            setTogglingBot(false);
        }
    };

    const handleActivateBot = async () => {
        if (!bot) return;
        setTogglingBot(true);
        try {
            const r = await fetch(`${API_BASE_URL}/api/referrals/bots/${bot.id}`, {
                method: 'PATCH',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ is_active: true }),
            });
            if (!r.ok) {
                const d = await r.json().catch(() => ({}));
                throw new Error(d.detail || 'Ошибка');
            }
            await loadBot();
        } catch (e) {
            alert(e.message || 'Ошибка');
        } finally {
            setTogglingBot(false);
        }
    };

    const handleDeductBalance = async () => {
        if (!bot || !bot.owner_user_id) return;
        const amt = parseFloat(String(deductAmount).replace(',', '.'));
        if (Number.isNaN(amt) || amt <= 0) {
            alert('Введите сумму списания больше 0');
            return;
        }
        if (!confirm(`Списать ${amt.toFixed(2)} ₽ с реферального баланса владельца?`)) return;
        setDeducting(true);
        try {
            const r = await fetch(`${API_BASE_URL}/api/referrals/owners/${bot.owner_user_id}/deduct-balance`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    amount_rub: amt,
                    note: deductNote.trim() || undefined,
                    referral_bot_id: bot.id,
                }),
            });
            if (!r.ok) {
                const d = await r.json().catch(() => ({}));
                throw new Error(d.detail || 'Ошибка списания');
            }
            setDeductAmount('');
            setDeductNote('');
            await loadBot();
        } catch (e) {
            alert(e.message || 'Ошибка');
        } finally {
            setDeducting(false);
        }
    };

    const handleApprove = async (requestId) => {
        if (!me || !me.id) { alert('Сессия не определена'); return; }
        const comment = window.prompt('Комментарий для пользователя (необязательно):') ?? '';
        setProcessingId(requestId);
        try {
            const r = await fetch(`${API_BASE_URL}/api/referrals/withdrawals/${requestId}/approve`, {
                method: 'PATCH',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ processed_by_user_id: me.id, admin_comment: comment || undefined }),
            });
            if (!r.ok) throw new Error((await r.json()).detail || 'Ошибка');
            await loadBot();
        } catch (e) {
            alert(e.message || 'Ошибка');
        } finally {
            setProcessingId(null);
        }
    };

    const handleReject = async (requestId) => {
        if (!me || !me.id) { alert('Сессия не определена'); return; }
        const comment = window.prompt('Причина отклонения / комментарий для пользователя (необязательно):') ?? '';
        setProcessingId(requestId);
        try {
            const r = await fetch(`${API_BASE_URL}/api/referrals/withdrawals/${requestId}/reject`, {
                method: 'PATCH',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ processed_by_user_id: me.id, admin_comment: comment || undefined }),
            });
            if (!r.ok) throw new Error((await r.json()).detail || 'Ошибка');
            await loadBot();
        } catch (e) {
            alert(e.message || 'Ошибка');
        } finally {
            setProcessingId(null);
        }
    };

    if (loading && !bot) {
        return (
            <div className="flex justify-center py-12">
                <RefreshCw className="animate-spin text-slate-400" size={32} />
            </div>
        );
    }
    if (error || !bot) {
        return (
            <div className="space-y-4">
                <button onClick={() => navigate('/partner-bots')} className="flex items-center gap-2 text-slate-600 hover:text-slate-800">
                    <ArrowLeft size={18} /> Назад к списку
                </button>
                <div className="bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 rounded-lg">{error || 'Бот не найден'}</div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-4">
                <button onClick={() => navigate('/partner-bots')} className="flex items-center gap-2 text-slate-600 hover:text-slate-800">
                    <ArrowLeft size={18} /> Назад к списку
                </button>
                <button onClick={loadBot} disabled={loading} className="flex items-center gap-2 bg-slate-800 text-white px-4 py-2 rounded-lg hover:bg-slate-700 disabled:opacity-50">
                    <RefreshCw size={18} className={loading ? 'animate-spin' : ''} /> Обновить
                </button>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
                <div className="p-6 space-y-4">
                    <h3 className="text-lg font-semibold text-slate-800">Данные бота</h3>
                    <dl className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        <dt className="text-slate-500">Токен</dt>
                        <dd className="font-mono text-slate-800 break-all">{bot.token || '—'}</dd>
                        <dt className="text-slate-500">Username бота</dt>
                        <dd className="font-mono text-indigo-600">@{bot.bot_username || '—'}</dd>
                        <dt className="text-slate-500">Владелец (user_id)</dt>
                        <dd>{bot.owner_user_id}</dd>
                        <dt className="text-slate-500">Username владельца</dt>
                        <dd>@{bot.owner_username || '—'} (tg_id: {bot.owner_tg_id})</dd>
                        <dt className="text-slate-500">Статус бота (API / runner)</dt>
                        <dd>
                            {bot.is_active ? (
                                <span className="text-green-700 font-medium">Активен</span>
                            ) : (
                                <span className="text-amber-700 font-medium">Отключён</span>
                            )}
                        </dd>
                        <dt className="text-slate-500">Наценка</dt>
                        <dd>{bot.margin_percent}%</dd>
                        <dt className="text-slate-500">Базовая цена (из /prices)</dt>
                        <dd>{parseFloat(bot.referral_base_price || 0).toFixed(2)} ₽</dd>
                        <div className="md:col-span-2 flex items-center gap-2">
                            <input
                                type="number"
                                step="0.5"
                                min="1"
                                max="50"
                                defaultValue={bot.margin_percent}
                                id="margin-edit"
                                className="w-24 rounded border border-slate-200 px-2 py-1 text-sm"
                            />
                            <button
                                onClick={async () => {
                                    const val = parseFloat(document.getElementById('margin-edit')?.value);
                                    if (isNaN(val) || val < 1 || val > 50) { alert('Введите число от 1 до 50'); return; }
                                    try {
                                        const r = await fetch(`${API_BASE_URL}/api/referrals/bots/${bot.id}`, {
                                            method: 'PATCH',
                                            credentials: 'include',
                                            headers: { 'Content-Type': 'application/json' },
                                            body: JSON.stringify({ margin_percent: val }),
                                        });
                                        if (!r.ok) throw new Error((await r.json()).detail || 'Ошибка');
                                        await loadBot();
                                    } catch (e) { alert(e.message); }
                                }}
                                className="px-3 py-1.5 bg-indigo-100 text-indigo-800 rounded-lg text-sm hover:bg-indigo-200"
                            >
                                Сохранить наценку
                            </button>
                        </div>
                        <dt className="text-slate-500">Цена 1 ⭐ (СБП)</dt>
                        <dd>{parseFloat(bot.star_price_sbp || 0).toFixed(2)} ₽</dd>
                        <dt className="text-slate-500">Заказов (успешных)</dt>
                        <dd>{bot.orders_count ?? 0}</dd>
                        <dt className="text-slate-500">Пользователей перешло в бота</dt>
                        <dd>{bot.users_total ?? 0}</dd>
                        <dt className="text-slate-500">Выручка</dt>
                        <dd className="font-semibold text-green-600">{parseFloat(bot.total_revenue || 0).toLocaleString('ru-RU', { minimumFractionDigits: 2 })} ₽</dd>
                        <dt className="text-slate-500">Реферальный баланс владельца</dt>
                        <dd>{parseFloat(bot.owner_referral_balance || 0).toLocaleString('ru-RU', { minimumFractionDigits: 2 })} ₽</dd>
                    </dl>
                    {bot.owner_user_id && (
                        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-2">
                            <h4 className="text-sm font-semibold text-slate-800">Списание с реферального баланса</h4>
                            <p className="text-xs text-slate-500">Средства списываются у владельца бота; операция попадает в журнал (admin_adjustment).</p>
                            <div className="flex flex-wrap gap-2 items-end">
                                <div>
                                    <label className="block text-xs text-slate-500">Сумма, ₽</label>
                                    <input
                                        type="text"
                                        inputMode="decimal"
                                        value={deductAmount}
                                        onChange={(e) => setDeductAmount(e.target.value)}
                                        placeholder="0.00"
                                        className="w-32 rounded border border-slate-200 px-2 py-1.5 text-sm"
                                    />
                                </div>
                                <div className="flex-1 min-w-[200px]">
                                    <label className="block text-xs text-slate-500">Комментарий</label>
                                    <input
                                        type="text"
                                        value={deductNote}
                                        onChange={(e) => setDeductNote(e.target.value)}
                                        placeholder="Необязательно"
                                        className="w-full rounded border border-slate-200 px-2 py-1.5 text-sm"
                                    />
                                </div>
                                <button
                                    type="button"
                                    onClick={handleDeductBalance}
                                    disabled={deducting}
                                    className="px-4 py-2 bg-amber-100 text-amber-900 rounded-lg hover:bg-amber-200 disabled:opacity-50 text-sm"
                                >
                                    {deducting ? '…' : 'Списать'}
                                </button>
                            </div>
                        </div>
                    )}
                    <div className="flex flex-wrap gap-2">
                        {bot.is_active ? (
                            <button
                                onClick={handleDeactivateBot}
                                disabled={togglingBot}
                                className="flex items-center gap-2 px-4 py-2 bg-rose-100 text-rose-700 rounded-lg hover:bg-rose-200 disabled:opacity-50"
                            >
                                <Ban size={16} /> Отключить бота (API)
                            </button>
                        ) : (
                            <button
                                onClick={handleActivateBot}
                                disabled={togglingBot}
                                className="flex items-center gap-2 px-4 py-2 bg-green-100 text-green-800 rounded-lg hover:bg-green-200 disabled:opacity-50"
                            >
                                <Check size={16} /> Включить бота
                            </button>
                        )}
                        <button
                            onClick={handleDelete}
                            disabled={deleting}
                            className="flex items-center gap-2 px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 disabled:opacity-50"
                        >
                            <Trash2 size={16} /> Удалить бота
                        </button>
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
                <div className="p-6">
                    <h3 className="text-lg font-semibold text-slate-800 mb-4">UTM-ссылки и аналитика</h3>
                    <div className="mb-4 rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm text-slate-600">
                        Формула цены: <strong>{parseFloat(bot.referral_base_price || 0).toFixed(2)} ₽</strong> (база) x
                        (1 + наценка партнёра/100 + надбавка способа оплаты/100).
                    </div>
                    <div className="flex flex-wrap gap-2 items-end mb-4 text-sm">
                        <div>
                            <label className="block text-xs text-slate-500">Метрики за период — с</label>
                            <input type="date" value={utmDateFrom} onChange={(e) => setUtmDateFrom(e.target.value)} className="rounded border border-slate-200 px-2 py-1.5" />
                        </div>
                        <div>
                            <label className="block text-xs text-slate-500">по</label>
                            <input type="date" value={utmDateTo} onChange={(e) => setUtmDateTo(e.target.value)} className="rounded border border-slate-200 px-2 py-1.5" />
                        </div>
                        <button type="button" onClick={loadUtmLinks} className="px-3 py-1.5 bg-slate-100 rounded-lg hover:bg-slate-200">Обновить</button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-2 mb-3">
                        <input value={utmName} onChange={(e) => setUtmName(e.target.value)} placeholder="Название" className="rounded border border-slate-200 px-3 py-2 text-sm" />
                        <input value={utmKey} onChange={(e) => setUtmKey(e.target.value)} placeholder="utm_key (уник.)" className="rounded border border-slate-200 px-3 py-2 text-sm" />
                        <input value={utmSource} onChange={(e) => setUtmSource(e.target.value)} placeholder="utm_source" className="rounded border border-slate-200 px-3 py-2 text-sm" />
                        <input value={utmMedium} onChange={(e) => setUtmMedium(e.target.value)} placeholder="utm_medium" className="rounded border border-slate-200 px-3 py-2 text-sm" />
                        <input value={utmCampaign} onChange={(e) => setUtmCampaign(e.target.value)} placeholder="utm_campaign" className="rounded border border-slate-200 px-3 py-2 text-sm" />
                    </div>
                    <button
                        onClick={handleCreateUtm}
                        disabled={creatingUtm}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 mb-4"
                    >
                        Создать UTM ссылку
                    </button>
                    {utmLinks.length === 0 ? (
                        <p className="text-slate-500">UTM-ссылок пока нет.</p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full text-sm">
                                <thead>
                                    <tr className="border-b">
                                        <th className="text-left py-2">Название</th>
                                        <th className="text-left py-2">Ссылка</th>
                                        <th className="text-left py-2">Клики</th>
                                        <th className="text-left py-2">Уники</th>
                                        <th className="text-left py-2">Заказы</th>
                                        <th className="text-left py-2">Выручка</th>
                                        <th className="text-left py-2">Период: уники</th>
                                        <th className="text-left py-2">Период: заказы</th>
                                        <th className="text-left py-2">Период: ₽</th>
                                        <th className="text-left py-2" />
                                    </tr>
                                </thead>
                                <tbody>
                                    {utmLinks.map((u) => (
                                        <tr key={u.id} className="border-b border-slate-100">
                                            <td className="py-2">{u.name}</td>
                                            <td className="py-2">
                                                {u.generated_link ? (
                                                    <a className="text-indigo-600 underline break-all" href={u.generated_link} target="_blank" rel="noreferrer">{u.generated_link}</a>
                                                ) : '—'}
                                            </td>
                                            <td>{u.clicks}</td>
                                            <td>{u.unique_users}</td>
                                            <td>{u.orders_count}</td>
                                            <td>{parseFloat(u.revenue || 0).toFixed(2)} ₽</td>
                                            <td>{u.period ? u.period.period_new_uniques : '—'}</td>
                                            <td>{u.period ? u.period.period_orders : '—'}</td>
                                            <td>{u.period ? parseFloat(u.period.period_revenue || 0).toFixed(2) : '—'}</td>
                                            <td className="py-2 whitespace-nowrap">
                                                <button
                                                    type="button"
                                                    onClick={() => setEditingUtm({ ...u })}
                                                    className="text-indigo-600 hover:underline mr-2 inline-flex items-center gap-1"
                                                >
                                                    <Pencil size={14} /> Изм.
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => handleDeleteUtm(u.id)}
                                                    className="text-rose-600 hover:underline"
                                                >
                                                    Удалить
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    {editingUtm && (
                        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setEditingUtm(null)}>
                            <div className="bg-white rounded-xl shadow-lg max-w-lg w-full p-6 space-y-3" onClick={(e) => e.stopPropagation()}>
                                <h4 className="font-semibold text-slate-800">Редактирование UTM</h4>
                                <p className="text-xs text-slate-500">Ключ <code className="bg-slate-100 px-1">{editingUtm.utm_key}</code> менять нельзя (ссылки в Telegram).</p>
                                <label className="block text-sm">Название</label>
                                <input className="w-full border rounded px-3 py-2 text-sm" value={editingUtm.name || ''} onChange={(e) => setEditingUtm((x) => ({ ...x, name: e.target.value }))} />
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <label className="text-xs text-slate-500">utm_source</label>
                                        <input className="w-full border rounded px-2 py-1.5 text-sm" value={editingUtm.utm_source || ''} onChange={(e) => setEditingUtm((x) => ({ ...x, utm_source: e.target.value }))} />
                                    </div>
                                    <div>
                                        <label className="text-xs text-slate-500">utm_medium</label>
                                        <input className="w-full border rounded px-2 py-1.5 text-sm" value={editingUtm.utm_medium || ''} onChange={(e) => setEditingUtm((x) => ({ ...x, utm_medium: e.target.value }))} />
                                    </div>
                                    <div>
                                        <label className="text-xs text-slate-500">utm_campaign</label>
                                        <input className="w-full border rounded px-2 py-1.5 text-sm" value={editingUtm.utm_campaign || ''} onChange={(e) => setEditingUtm((x) => ({ ...x, utm_campaign: e.target.value }))} />
                                    </div>
                                    <div>
                                        <label className="text-xs text-slate-500">utm_content</label>
                                        <input className="w-full border rounded px-2 py-1.5 text-sm" value={editingUtm.utm_content || ''} onChange={(e) => setEditingUtm((x) => ({ ...x, utm_content: e.target.value }))} />
                                    </div>
                                    <div className="col-span-2">
                                        <label className="text-xs text-slate-500">utm_term</label>
                                        <input className="w-full border rounded px-2 py-1.5 text-sm" value={editingUtm.utm_term || ''} onChange={(e) => setEditingUtm((x) => ({ ...x, utm_term: e.target.value }))} />
                                    </div>
                                </div>
                                <div className="flex gap-2 justify-end pt-2">
                                    <button type="button" className="px-4 py-2 rounded-lg border" onClick={() => setEditingUtm(null)}>Отмена</button>
                                    <button type="button" className="px-4 py-2 rounded-lg bg-indigo-600 text-white" onClick={handleSaveUtmEdit}>Сохранить</button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {bot.transactions && bot.transactions.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
                    <div className="p-6">
                        <h3 className="text-lg font-semibold text-slate-800 mb-4">Транзакции (заказы через бота)</h3>
                        <div className="overflow-x-auto">
                            <table className="min-w-full text-sm">
                                <thead><tr className="border-b"><th className="text-left py-2">ID</th><th className="text-left py-2">Сумма</th><th className="text-left py-2">Звёзд</th><th className="text-left py-2">Статус</th><th className="text-left py-2">Дата</th></tr></thead>
                                <tbody>
                                    {bot.transactions.map((t) => (
                                        <tr key={t.id} className="border-b border-slate-100">
                                            <td className="py-2">{t.id}</td>
                                            <td>{parseFloat(t.amount || 0).toFixed(2)} ₽</td>
                                            <td>{t.quantity}</td>
                                            <td>{t.status}</td>
                                            <td className="text-slate-500">{t.created_at ? new Date(t.created_at).toLocaleString('ru-RU') : '—'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
                <div className="p-6">
                    <h3 className="text-lg font-semibold text-slate-800 mb-4">Заявки на вывод</h3>
                    {!bot.withdrawals || bot.withdrawals.length === 0 ? (
                        <p className="text-slate-500">Заявок нет.</p>
                    ) : (
                        <ul className="space-y-3">
                            {bot.withdrawals.map((w) => (
                                <li key={w.id} className="flex flex-wrap items-center justify-between gap-2 p-3 bg-slate-50 rounded-lg">
                                    <div>
                                        <span className="font-semibold">{parseFloat(w.amount).toFixed(2)} ₽</span>
                                        <span className="ml-2 text-slate-500 text-sm">{w.withdrawal_type || 'usdt'}</span>
                                        <span className="ml-2 text-slate-500 text-sm">{w.status}</span>
                                        {w.payment_details && <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">{w.payment_details}</p>}
                                        {w.admin_comment && <p className="text-sm text-indigo-600 mt-1">💬 {w.admin_comment}</p>}
                                        {w.created_at && <p className="text-xs text-slate-400">{w.created_at}</p>}
                                    </div>
                                    {w.status === 'pending' && (
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => handleApprove(w.id)}
                                                disabled={processingId !== null}
                                                className="flex items-center gap-1 px-3 py-1.5 bg-green-100 text-green-800 rounded hover:bg-green-200 disabled:opacity-50"
                                            >
                                                <Check size={14} /> Выполнено
                                            </button>
                                            <button
                                                onClick={() => handleReject(w.id)}
                                                disabled={processingId !== null}
                                                className="flex items-center gap-1 px-3 py-1.5 bg-rose-100 text-rose-800 rounded hover:bg-rose-200 disabled:opacity-50"
                                            >
                                                <X size={14} /> Отклонить
                                            </button>
                                        </div>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PartnerBotDetailPage;
